import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui';

export function LoginPage() {
  // Surfaced after the reset flow redirects here.
  const justReset = typeof window !== 'undefined' && window.location.search.includes('reset=success');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(username, password, totp || undefined);
      // Hard navigate so every query starts clean for the new session.
      window.location.assign('/');
    } catch (err) {
      if (err instanceof ApiError) {
        // Vikunja signals a required second factor via this message.
        if (/totp|two.?factor/i.test(err.message)) {
          setNeedsTotp(true);
          setError('Bitte den Code aus der Authenticator-App eingeben.');
        } else if (err.status === 412 || err.status === 401 || err.status === 403) {
          setError('Benutzername oder Passwort ist falsch.');
        } else if (err.status === 429) {
          // Vikunja's noauthlimit is 10/min per IP on login and cannot be
          // disabled, so say what's actually happening.
          setError('Zu viele Anmeldeversuche. Bitte eine Minute warten.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Verbindung zum Server fehlgeschlagen.');
      }
      setBusy(false);
    }
  }

  return (
    // Entirely white splash, in both light and dark mode. The card's soft
    // shadow still gives it definition against the white page.
    <div className="flex min-h-dvh items-center justify-center bg-white px-4">
      <div className="w-full max-w-[420px]">
        {/* Match the live app's login card: white, 16px radius, soft shadow,
            the Ecotone logo as the only branding (no title text). */}
        <div className="rounded-2xl bg-white p-10 shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
          <img
            src="/ecotone-logo.jpeg"
            alt="Ecotone"
            className="mx-auto mb-8 block w-[180px]"
          />
          <form onSubmit={onSubmit} className="space-y-4 text-left">
          <Field
            label="Benutzername"
            value={username}
            onChange={setUsername}
            autoComplete="username"
            autoFocus
            required
          />
          <Field
            label="Passwort"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />
          {needsTotp && (
            <Field
              label="Authenticator-Code"
              value={totp}
              onChange={setTotp}
              autoComplete="one-time-code"
              inputMode="numeric"
              required
            />
          )}

          {justReset && !error && (
            <p className="rounded-lg bg-brand-50 px-3 py-2 text-[13px] text-brand-800 dark:bg-brand-900/30 dark:text-brand-200">
              Passwort gespeichert. Du kannst dich jetzt anmelden.
            </p>
          )}

          {error && (
            <p
              className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700
                         dark:bg-red-950/40 dark:text-red-300"
              role="alert"
            >
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" loading={busy} className="w-full">
            Anmelden
          </Button>

            <a href="/reset" className="block text-center text-xs text-gray-500 hover:text-gray-800">
              Passwort vergessen oder noch keines festgelegt?
            </a>
          </form>

          <p className="mt-6 text-center text-xs text-gray-400">
            Zugang über das bestehende Ecotone-Konto.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  ...rest
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  return (
    // Fixed light-scheme colours: the card is always white, so inputs must be
    // dark-on-white regardless of the app's dark-mode setting.
    <label className="block">
      <span className="mb-1 block text-[13px] font-medium text-gray-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900
                   placeholder:text-gray-400 focus:border-brand-500 focus:outline-none
                   focus:ring-1 focus:ring-brand-500"
        {...rest}
      />
    </label>
  );
}
